import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import cx from 'classnames';
import { getFlyingBetslipConfig } from 'helpers/prematch';

import { Layout } from '@atlas/components';
import {
  BetslipProvider,
  Fuse,
  GlobalBetslipProvider,
  SiteConfigManager,
  useApplicationState,
} from '@atlas/core';
import { Loader, ScrollableViewportObserver } from '@atlas/ui';

import ExtendedFlyingBetslip from 'common/Betslip/ExtendedFlyingBetslip';
import {
  getLocalBetslipState,
  LocalBetslipContextProvider,
} from 'common/Betslip/LocalBetslipContext';
import NotificationStatePopup from 'common/Popup/NotificationStatePopup';
import SimplifiedSportsbookViewProvider from 'common/SimplifiedSportsbook/SimplifiedSportsbookViewProvider';
import CustomStylesPortal from 'components/CustomStylesPortal/CustomStylesPortal';
import { NAVIGATION_ACTIONS_CONFIG } from 'components/Layout/Layout';
import QueryRoutes from 'components/Layout/QueryRoutes';
import { useActionOnNavigation } from 'components/Layout/useActionOnNavigation';
import useThemeBootstrap from 'components/Layout/useThemeBootstrap';
import { useSearchInputState } from 'components/Search/context/InputProvider';
import IFrameFlyingBetslip from 'iframe/IFrameFlyingBetslip';
import { OddsFormatProvider } from 'src/components/Pages/Menu/OddsFormatSettings/OddsFormatProvider';

import IFrameBetslipContainer from './IFrameBetslipContainer';
import IFrameHeader from './IFrameHeader';
import IFrameNavigation from './IFrameNavigation';

const LayoutErrorFallback = props => {
  const { error = '' } = props;

  // TODO: Update layout internal error view
  return (
    <div>
      <div>Oops, something went wrong</div>
      <div>{error.message}</div>
    </div>
  );
};

const IFrameLayout = ({ children }) => {
  const { pathname } = useLocation();
  const localBetslipState = getLocalBetslipState();
  const { connection } = useApplicationState();
  const { focused: fucusedSearch } = useSearchInputState();
  useThemeBootstrap();

  const cmsBetslipSettings = SiteConfigManager.getConfig('config.config.betslip', {});
  const showTopNavigation = SiteConfigManager.getConfig(
    'config.config.sportsbook.showTopNavigationMobile',
  );
  const showBottomNavigation = SiteConfigManager.getConfig(
    'config.config.sportsbook.showBottomNavigationMobile',
  );

  const betslipNavConfig = useMemo(() => getFlyingBetslipConfig(), []);

  const layoutClassName = cx(
    'layout layout-iframe',
    { 'no-navbar': !showBottomNavigation },
    { disconnected: !connection.connected },
    { results: pathname.includes('results') },
  );

  const layoutWrapperClassName = cx('layout-wrapper', {
    'no-top-nav': !showTopNavigation,
    'search-enabled': fucusedSearch,
  });

  useActionOnNavigation(NAVIGATION_ACTIONS_CONFIG);

  return (
    <Fuse fallback={LayoutErrorFallback}>
      <CustomStylesPortal />
      <GlobalBetslipProvider>
        <BetslipProvider settings={cmsBetslipSettings}>
          <LocalBetslipContextProvider value={localBetslipState}>
            <OddsFormatProvider>
              <Layout>
                <Layout.Header sticky={showTopNavigation}>
                  {showTopNavigation && <IFrameHeader />}
                </Layout.Header>
                <Layout.Content>
                  <SimplifiedSportsbookViewProvider>
                    <ScrollableViewportObserver className={layoutWrapperClassName} id="content">
                      <div className={layoutClassName}>
                        <AuthorizationLoader>{children}</AuthorizationLoader>

                        <div className="nav-notch-spacer" />
                      </div>
                    </ScrollableViewportObserver>

                    {betslipNavConfig.hasSimple && (
                      <IFrameFlyingBetslip showBottomNavigation={showBottomNavigation} />
                    )}
                    {betslipNavConfig.hasExtended && <ExtendedFlyingBetslip />}
                  </SimplifiedSportsbookViewProvider>
                </Layout.Content>
                <Layout.BottomMenu>
                  {showBottomNavigation && <IFrameNavigation />}
                </Layout.BottomMenu>
              </Layout>
              <IFrameBetslipContainer />
            </OddsFormatProvider>
          </LocalBetslipContextProvider>
          <QueryRoutes />
        </BetslipProvider>
      </GlobalBetslipProvider>
    </Fuse>
  );
};

const AuthorizationLoader = ({ children }) => {
  const { resolved } = useApplicationState();

  if (resolved) {
    return children;
  }

  return (
    <div className="global-loader">
      <NotificationStatePopup message="please wait" title="authenticating">
        <Loader className="medium" />
      </NotificationStatePopup>
    </div>
  );
};

export default IFrameLayout;
